/**
 * 店舗選択 Redux Slice
 *
 * ログイン時に自動解決される組織階層を
 * アプリ全体で共有するための状態管理。
 * localStorage で永続化し、ページ遷移・リロード時も保持する。
 *
 * # orgLevels の key 仕様（schemaVersion=2 以降）
 * key は **organization.level** に対応する数値:
 *   - 0..9 : 上位階層（organization_level の first..tenth フラグに対応）
 *   - 13   : 課（organization_level.section フラグに対応）
 * level=11 (店舗) と level=12 (部門) は orgLevels には含めず、
 * 専用フィールド `store` / `department` に保持する。
 */
import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

/** 「全選択」を表すセンチネルID（DB の ID は 1 始まりなので衝突しない） */
export const ALL_SELECTION_ID = 0;

/** localStorage に保存するスキーマバージョン（v2: orgLevels の key を depth → organization.level に変更） */
const SCHEMA_VERSION = 2;

/** 指定された選択項目が「全選択」かどうかを判定 */
export function isAllSelection(item: StoreSelectionItem | null | undefined): item is StoreSelectionItem {
    return item?.id === ALL_SELECTION_ID;
}

/** 各階層の選択項目 */
export interface StoreSelectionItem {
    id: number;
    organizationId?: number;
    name: string;
}

/** 店舗選択の状態（組織階層は動的レベル対応） */
export interface StoreSelectionState {
    /** 永続化スキーマバージョン */
    schemaVersion?: number;
    /** 組織階層の選択状態（key = organization.level: 0..9, 13） */
    orgLevels: Record<number, StoreSelectionItem | null>;
    /** 店舗選択 (organization.level=11) */
    store: StoreSelectionItem | null;
    /** 部門選択 (organization.level=12) */
    department: StoreSelectionItem | null;
}

const STORAGE_KEY = "storeSelection";

const emptyState: StoreSelectionState = {
    schemaVersion: SCHEMA_VERSION,
    orgLevels: {},
    store: null,
    department: null,
};

/** 旧フォーマット（topArea等）の型ガード */
interface LegacyV0State {
    topArea?: StoreSelectionItem | null;
    middleArea?: StoreSelectionItem | null;
    bottomArea?: StoreSelectionItem | null;
    subArea?: StoreSelectionItem | null;
    store?: StoreSelectionItem | null;
    department?: StoreSelectionItem | null;
}

function isLegacyV0Format(obj: Record<string, unknown>): boolean {
    return "topArea" in obj && !("orgLevels" in obj);
}

/**
 * v1 形式（depth ベースの orgLevels: key=1,2,3,...）からの判定.
 * `schemaVersion` フィールドが存在しない、かつ orgLevels が non-empty なら v1 とみなす。
 */
function isV1Format(obj: Record<string, unknown>): boolean {
    if (typeof obj.schemaVersion === "number" && obj.schemaVersion >= SCHEMA_VERSION) {
        return false;
    }
    return "orgLevels" in obj;
}

/**
 * 旧 v0 (topArea 等) → v2 マイグレーション.
 * topArea/middleArea 等は depth 由来の意味なので、新スキーマでは解釈不能。
 * orgLevels はリセットし、store/department のみ保持する。
 */
function migrateLegacyV0(legacy: LegacyV0State): StoreSelectionState {
    return {
        schemaVersion: SCHEMA_VERSION,
        orgLevels: {},
        store: legacy.store ?? null,
        department: legacy.department ?? null,
    };
}

/**
 * v1 (depth ベース orgLevels) → v2 マイグレーション.
 * depth と organization.level は意味が異なるため、orgLevels はクリアする。
 * store / department フィールドは互換なのでそのまま保持。
 */
function migrateV1(v1: StoreSelectionState): StoreSelectionState {
    return {
        schemaVersion: SCHEMA_VERSION,
        orgLevels: {},
        store: v1.store ?? null,
        department: v1.department ?? null,
    };
}

/** localStorage から復元（旧フォーマットの自動マイグレーション付き） */
const loadFromStorage = (): StoreSelectionState => {
    try {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
            const parsed: unknown = JSON.parse(saved);
            if (parsed && typeof parsed === "object") {
                const obj = parsed as Record<string, unknown>;
                if (isLegacyV0Format(obj)) {
                    const migrated = migrateLegacyV0(obj as LegacyV0State);
                    localStorage.setItem(STORAGE_KEY, JSON.stringify(migrated));
                    return migrated;
                }
                if (isV1Format(obj)) {
                    // localStorage の v1 形式は型保証が無いので unknown 経由でキャスト。
                    // migrateV1 が新スキーマ形式に組み直す。
                    const migrated = migrateV1(obj as unknown as StoreSelectionState);
                    localStorage.setItem(STORAGE_KEY, JSON.stringify(migrated));
                    return migrated;
                }
                return parsed as unknown as StoreSelectionState;
            }
        }
    } catch {
        /* ignore parse errors */
    }
    return { ...emptyState };
};

const storeSelectionSlice = createSlice({
    name: "storeSelection",
    initialState: loadFromStorage(),
    reducers: {
        setStoreSelection(state, action: PayloadAction<StoreSelectionState>) {
            state.schemaVersion = SCHEMA_VERSION;
            state.orgLevels = action.payload.orgLevels;
            state.store = action.payload.store;
            state.department = action.payload.department;
            const persisted: StoreSelectionState = {
                schemaVersion: SCHEMA_VERSION,
                orgLevels: action.payload.orgLevels,
                store: action.payload.store,
                department: action.payload.department,
            };
            localStorage.setItem(STORAGE_KEY, JSON.stringify(persisted));
        },
        clearStoreSelection(state) {
            state.schemaVersion = SCHEMA_VERSION;
            state.orgLevels = {};
            state.store = null;
            state.department = null;
            localStorage.removeItem(STORAGE_KEY);
        },
    },
});

export const { setStoreSelection, clearStoreSelection } = storeSelectionSlice.actions;
export default storeSelectionSlice.reducer;
